




/// <reference types="google.maps" />
import { Component, Vue, Prop } from 'vue-property-decorator';


@Component
export default class GoogleMap extends Vue {
  @Prop({ required: true })
  program!: {
    address: {
      latitude: number;
      longitude: number;
    };
  };

  // Displayed in the program marker's info window when clicked.
  @Prop({ default: '' }) programName!: string;
  @Prop({ default: '' }) programAddress!: string;

  // This will hold the Google Map instance.
  map: google.maps.Map | null = null;

  // Single reused info window, opened on whichever marker was clicked.
  infoWindow: google.maps.InfoWindow | null = null;

  // This array will store all the POI markers we add to the map.
  allPoiMarkers: google.maps.Marker[] = [];
  programMarker: google.maps.Marker | null = null;

  // Define the categories for which you want to load POIs.
  //categories: string[] = ['school', 'restaurant', 'bus_station', 'gym', 'grocery_or_supermarket'];


  @Prop({ default: 5 }) radiusKm!: number;


  @Prop({ default: () => [] })
   categories!: string[];
  
  nearest: {
  [category: string]: { name: string; distanceKm: string }[] } = {};

  beforeDestroy() {
    this.allPoiMarkers.forEach(m => m.setMap(null));
    this.allPoiMarkers = [];
    if (this.programMarker) {
        this.programMarker.setMap(null);
        this.programMarker = null;
    }
    if (this.infoWindow) {
        this.infoWindow.close();
        this.infoWindow = null;
    }
    this.map = null;
  }

  // Opens the shared info window with the given HTML content, anchored
  // to the given marker (closes/moves any previously open one).
  openInfoWindow(marker: google.maps.Marker, html: string) {
    if (!this.infoWindow || !this.map) return;
    this.infoWindow.setContent(html);
    this.infoWindow.open(this.map, marker);
  }

  mounted() {
    if (typeof google !== 'undefined') {
      const lat = this.program.address.latitude;
      const lng = this.program.address.longitude;
      const mapOptions: google.maps.MapOptions = {
        center: { lat, lng },
        zoom: 16,
        minZoom: 14,
        maxZoom: 18,
      };

      // Create the map in the container.
      this.map = new google.maps.Map(this.$refs.mapContainer as HTMLElement, mapOptions);
      this.infoWindow = new google.maps.InfoWindow();

      // Add a marker for the program's location.
      const primaryColor = this.getThemePrimaryColor();
      this.programMarker = new google.maps.Marker({
        position: { lat, lng },
        map: this.map,
        title: this.programName || 'Programme',
        icon: this.buildHomePin(primaryColor),
        zIndex: 999,
      });
      this.programMarker.addListener('click', () => {
        const name = this.programName || 'Emplacement du programme';
        const address = this.programAddress
          ? `<div style="font-size:12px;color:#6b7280;line-height:1.5;margin-top:2px;">${this.programAddress}</div>`
          : '';
        this.openInfoWindow(this.programMarker as google.maps.Marker, `
          <div style="font-family:inherit;min-width:170px;padding:2px 4px;">
            <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;">
              <span style="width:22px;height:22px;border-radius:6px;background:${primaryColor};display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;">
                <i class="fas fa-home" style="color:#ffffff;font-size:10px;"></i>
              </span>
              <span style="font-size:10.5px;font-weight:700;color:${primaryColor};text-transform:uppercase;letter-spacing:0.4px;">Programme</span>
            </div>
            <div style="font-weight:700;font-size:13px;color:#1f2937;">${name}</div>
            ${address}
          </div>
        `);
      });

      // Fetch and add POIs to the map — one search per category, the
      // 3 closest results get the "featured" pin + power the sidebar list.
      this.categories.forEach(cat => this.loadCategoryPOIs(cat));
    } else {
      console.error('Google Maps API is not loaded.');
    }
  }





  // Single search per category: builds every marker (closest 3 get the
  // "featured" pin) AND the sidebar's nearest-3 list, from the same
  // result set — so the list and the highlighted pins always match.
  loadCategoryPOIs(category: string) {
            if (!this.map) return;

            const center = new google.maps.LatLng(
              this.program.address.latitude,
              this.program.address.longitude
            );
            const radius = this.radiusKm * 1000;
            const service = new google.maps.places.PlacesService(this.map);

            service.nearbySearch({ location: center, radius, type: category }, (results, status) => {
                if (status !== google.maps.places.PlacesServiceStatus.OK || !results) {
                    console.error(`Error retrieving POIs for category "${category}":`, status);
                    this.$set(this.nearest, category, []);
                    this.$emit('nearestUpdates', this.nearest);
                    return;
                }

                const sorted = results
                    .filter(p => p.geometry && p.geometry.location)
                    .map(p => ({
                        place: p,
                        distMeters: google.maps.geometry.spherical.computeDistanceBetween(
                            center,
                            p.geometry!.location!
                        ),
                    }))
                    .sort((a, b) => a.distMeters - b.distMeters);

                const group = this.getCategoryGroup(category);
                const color = this.categoryColors[group];
                const glyph = this.categoryGlyphs[group];
                const groupIcon = this.categoryGroupIcons[group];
                const typeLabel = this.categoryLabels[category] || category;

                sorted.forEach(({ place, distMeters }, index) => {
                    const isNearest = index < 3;
                    const marker = new google.maps.Marker({
                        position: place.geometry!.location!,
                        map: this.map,
                        title: place.name,
                        icon: this.buildCategoryPin(color, glyph, isNearest),
                        zIndex: isNearest ? 500 : undefined,
                    });
                    // Attach the category for potential future filtering.
                    (marker as any).poiCategory = category;
                    this.allPoiMarkers.push(marker);

                    const distanceKm = (distMeters / 1000).toFixed(1);
                    marker.addListener('click', () => {
                        this.openInfoWindow(marker, `
                            <div style="font-family:inherit;min-width:170px;padding:2px 4px;">
                                <div style="display:flex;align-items:center;gap:6px;margin-bottom:6px;">
                                    <span style="width:22px;height:22px;border-radius:6px;background:${color};display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;">
                                        <i class="fas ${groupIcon}" style="color:#ffffff;font-size:10px;"></i>
                                    </span>
                                    <span style="font-size:10.5px;font-weight:700;color:${color};text-transform:uppercase;letter-spacing:0.4px;">${typeLabel}</span>
                                </div>
                                <div style="font-weight:700;font-size:13px;color:#1f2937;">${place.name || ''}</div>
                                <div style="font-size:12px;color:#6b7280;margin-top:2px;">${distanceKm} km</div>
                            </div>
                        `);
                    });
                });

                const nearest = sorted.slice(0, 3).map(({ place, distMeters }) => ({
                    name: place.name!,
                    distanceKm: (distMeters / 1000).toFixed(1),
                }));
                this.$set(this.nearest, category, nearest);
                this.$emit('nearestUpdates', this.nearest);
            });
            }

            // Simple, dependency-free glyphs drawn directly as SVG shapes (centered on 0,0).
            // Kept deliberately minimal so they stay crisp at marker size.
            categoryGlyphs: Record<string, string> = {
              transport: `<rect x="-6" y="-5" width="12" height="9" rx="2.5"/>
                          <rect x="-4.5" y="-3" width="3" height="3" fill="#ffffff"/>
                          <rect x="1.5" y="-3" width="3" height="3" fill="#ffffff"/>
                          <circle cx="-3.5" cy="5" r="1.3" fill="#ffffff"/>
                          <circle cx="3.5" cy="5" r="1.3" fill="#ffffff"/>`,
              ecoles: `<path d="M0,-6 L7,-2 L0,2 L-7,-2 Z"/>
                       <rect x="-1" y="2" width="2" height="4.5" rx="1"/>
                       <circle cx="1" cy="6.7" r="1" fill="#ffffff"/>`,
              resto: `<rect x="-4.2" y="-6" width="1.4" height="5" rx="0.7"/>
                      <rect x="-0.7" y="-6" width="1.4" height="5" rx="0.7"/>
                      <rect x="2.8" y="-6" width="1.4" height="5" rx="0.7"/>
                      <rect x="-0.7" y="-1.5" width="1.4" height="8" rx="0.7"/>`,
              marche: `<path d="M-5,-2 L5,-2 L4,6 L-4,6 Z"/>
                       <path d="M-2.5,-2 C-2.5,-6 2.5,-6 2.5,-2" fill="none" stroke-width="1.6" stroke-linecap="round"/>`,
              sport: `<rect x="-6" y="-1.3" width="12" height="2.6" rx="1"/>
                      <circle cx="-6" cy="0" r="3"/>
                      <circle cx="6" cy="0" r="3"/>`,
              sante: `<rect x="-1.6" y="-6" width="3.2" height="12" rx="1"/>
                      <rect x="-6" y="-1.6" width="12" height="3.2" rx="1"/>`,
            };

            categoryColors: Record<string, string> = {
              transport: '#7c3aed',
              ecoles:    '#ef4444',
              resto:     '#f97316',
              marche:    '#3b82f6',
              sport:     '#22c55e',
              sante:     '#ec4899',
            };

            // Font Awesome icon shown in the info window's type badge (must stay
            // aligned with the picker's group icons in ProgramDetails.vue).
            categoryGroupIcons: Record<string, string> = {
              transport: 'fa-bus',
              ecoles:    'fa-graduation-cap',
              resto:     'fa-utensils',
              marche:    'fa-shopping-basket',
              sport:     'fa-dumbbell',
              sante:     'fa-heartbeat',
            };

            // Human-readable label for the *exact* POI type (not just its group),
            // shown in the info window — mirrors the picker's option labels.
            categoryLabels: Record<string, string> = {
              bus_station:    'Bus',
              tram_station:   'Tramway',
              subway_station: 'Métro',
              train_station:  'Gare',
              airport:        'Aéroport',
              day_care:            'Crèche',
              primary_school:      'École primaire',
              secondary_school:    'Collège / Lycée',
              university:          'École supérieure',
              restaurant: 'Restaurant / Brasserie',
              cafe:       'Café / Bar',
              grocery_or_supermarket: 'Marché / Supermarché',
              gym:     'Salle de sport',
              stadium: 'Complex Sportif',
              hospital: 'Hôpital',
              pharmacy: 'Pharmacie',
              doctor:   'Médecin',
              dentist:  'Dentiste',
              clinic:   'Clinique',
            };

            // Resolve which glyph/color group a given POI category belongs to.
            getCategoryGroup(category: string): string {
              const transport = ['bus_station','tram_station','subway_station','train_station','airport'];
              const ecoles    = ['day_care','primary_school','secondary_school','university'];
              const resto     = ['restaurant','cafe'];
              const marche    = ['grocery_or_supermarket'];
              const sport     = ['gym','stadium'];
              const sante     = ['hospital','pharmacy','doctor','dentist','clinic'];

              if (transport.includes(category)) return 'transport';
              if (ecoles.includes(category))    return 'ecoles';
              if (resto.includes(category))     return 'resto';
              if (marche.includes(category))    return 'marche';
              if (sport.includes(category))     return 'sport';
              if (sante.includes(category))     return 'sante';
              return 'sante';
            }

            // Reads the app's theme primary color (stored as "R, G, B" in --vs-primary).
            getThemePrimaryColor(): string {
              const rgb = getComputedStyle(document.documentElement)
                .getPropertyValue('--vs-primary')
                .trim();
              return rgb ? `rgb(${rgb})` : '#3b82f6';
            }

            // Modern teardrop pin: colored outline, white badge, colored glyph inside.
            // "featured" = one of the 3 closest — drawn bigger with a gold halo
            // ring and a small star badge so it stands out from the other pins.
            buildCategoryPin(color: string, glyph: string, featured: boolean = false): google.maps.Icon {
              const size = featured ? 46 : 36;
              const height = featured ? 60 : 47;
              const ring = featured
                ? `<circle cx="17" cy="15" r="12.5" fill="none" stroke="#fbbf24" stroke-width="2.5"/>`
                : '';
              const star = featured
                ? `<g transform="translate(27,7)">
                     <circle r="5.5" fill="#fbbf24" stroke="#ffffff" stroke-width="1.2"/>
                     <path d="M0,-2.8 L0.8,-0.9 L2.8,-0.9 L1.2,0.4 L1.8,2.5 L0,1.2 L-1.8,2.5 L-1.2,0.4 L-2.8,-0.9 L-0.8,-0.9 Z" fill="#ffffff"/>
                   </g>`
                : '';
              const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${height}" viewBox="0 0 34 44">
                <path d="M17 1C9.3 1 3 7.3 3 15c0 10.5 14 27 14 27s14-16.5 14-27C31 7.3 24.7 1 17 1z" fill="${color}" stroke="rgba(0,0,0,0.18)" stroke-width="1"/>
                ${ring}
                <circle cx="17" cy="15" r="10" fill="#ffffff"/>
                <g transform="translate(17,15)" fill="${color}" stroke="${color}">${glyph}</g>
                ${star}
              </svg>`;
              return {
                url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
                scaledSize: new google.maps.Size(size, height),
                anchor: new google.maps.Point(size / 2, height),
              };
            }

            // Bigger, solid pin used for the program's own location.
            buildHomePin(color: string): google.maps.Icon {
              const house = `<path d="M0,-7 L8,-1 L8,8 L3,8 L3,2 L-3,2 L-3,8 L-8,8 L-8,-1 Z" fill="#ffffff"/>`;
              const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="62" viewBox="0 0 34 44">
                <path d="M17 1C8.2 1 1 8.2 1 17c0 11.7 16 30 16 30s16-18.3 16-30C33 8.2 25.8 1 17 1z" fill="${color}" stroke="#ffffff" stroke-width="2"/>
                <g transform="translate(17,16)">${house}</g>
              </svg>`;
              return {
                url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
                scaledSize: new google.maps.Size(48, 62),
                anchor: new google.maps.Point(24, 62),
              };
            }







}
